import React, { ReactNode, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/store/store"
import { useGetMesasQuery, Mesa } from "src/gql/schema"
import {
    setMesa,
    setMesas,
    setTablesDimensions,
    toggleRestaurantDrawer,
    toggleRestaurantLoading,
} from "src/store/restaurant/restaurantSlice"
import { useListenCheckMesasSubscription } from "../../gql/schema"
import { useProfile } from "../hooks/useProfile"
import { requestWsReconnect } from "src/graphql"
import {
    TABLES_DIMENSIONS_SKELETON,
    TABLES_LENGTH_SKELETON,
    TABLES_POSITIONS_SKELETON,
} from "../constants/localStorage"

const TablesProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch()
    const { hotel_id } = useProfile()
    const mesas = useSelector((root: RootState) => root.restaurant.mesas)
    const isDrawerOpen = useSelector((root: RootState) => root.restaurant.isDrawerOpen)
    const prevDrawerOpen = useRef(false)
    const { data: mesasQueryData, refetch: refetchMesas, loading } = useGetMesasQuery({
        variables: { hotel_id },
        notifyOnNetworkStatusChange: true,
    })
    const { data } = useListenCheckMesasSubscription()

    useEffect(() => {
        if (mesasQueryData?.mesas) {
            const dims = {
                y: Math.max(...(mesasQueryData.mesas as Mesa[]).map((m) => m?.posicion?.x || 1)),
                x: Math.max(...(mesasQueryData.mesas as Mesa[]).map((m) => m?.posicion?.y || 1)),
            }
            dispatch(setMesas(mesasQueryData?.mesas as any))
            dispatch(setTablesDimensions(dims))
            // Se guarda el layout para que el skeleton pueda dibujar la misma cuadrícula
            // en la siguiente carga, en lugar de una fila de cards genéricas.
            localStorage.setItem(TABLES_LENGTH_SKELETON, String(mesasQueryData.mesas.length))
            localStorage.setItem(
                TABLES_POSITIONS_SKELETON,
                JSON.stringify((mesasQueryData.mesas as Mesa[]).map((m) => ({ x: m.posicion?.x || 0, y: m.posicion?.y || 0 })))
            )
            localStorage.setItem(TABLES_DIMENSIONS_SKELETON, JSON.stringify(dims))
        }
    }, [mesasQueryData])

    useEffect(() => {
        if (data?.checkMesas) {
            const updatedMesas = mesas.map((mesa) => {
                if (mesa.mesa_id === data?.checkMesas?.mesa_id) {
                    return data?.checkMesas
                }
                return mesa
            })
            dispatch(setMesas(updatedMesas as any))
        }
    }, [data])

    useEffect(() => {
        dispatch(toggleRestaurantLoading(loading))
    }, [loading])

    // Al cerrar el drawer se refetchean las mesas: `onClose` deja loading en true
    // (skeleton), y el setMesas del refetch es lo que lo vuelve a apagar.
    useEffect(() => {
        if (prevDrawerOpen.current && !isDrawerOpen) {
            refetchMesas()
        }
        prevDrawerOpen.current = isDrawerOpen
    }, [isDrawerOpen, refetchMesas])

    useEffect(() => {
        const resync = async (force: boolean) => {
            if (document.visibilityState !== "visible") return
            // La decisión es sincrónica y va antes de cualquier `await`, para que los
            // providers compartan la misma ronda de resync.
            const reconnection = requestWsReconnect({ force })
            // Solo se resincroniza si ya pasó el intervalo mínimo o si la conexión está
            // caída; si no, no se hace nada: ni socket nuevo, ni skeleton, ni refetch
            // (ver requestWsReconnect).
            if (!reconnection) return

            dispatch(toggleRestaurantDrawer(false))
            dispatch(setMesa(null))
            dispatch(toggleRestaurantLoading(true))
            // Se espera la confirmación de la conexión ANTES de refetchear: así, si algo
            // cambia mientras el refetch está en curso, la suscripción (ya viva) lo
            // recibe y esa actualización llega después, sin que el refetch la pise con un
            // snapshot más viejo.
            await reconnection
            refetchMesas()
        }
        const onVisibilityChange = () => resync(false)
        const onOnline = () => resync(true)
        document.addEventListener("visibilitychange", onVisibilityChange)
        window.addEventListener("online", onOnline)
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange)
            window.removeEventListener("online", onOnline)
        }
    }, [refetchMesas])

    return <>{children}</>
}

export default TablesProvider
